import React from 'react';
import { useSidebar } from '../context/SidebarContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cacheManager } from '../hooks/GlobalCacheManager';
import '../styles/Sidebar.css';
import { VERSION } from '../constants/version';
import { signOut } from 'firebase/auth';
import { auth } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import { FaSignOutAlt } from 'react-icons/fa';  
import { 
  FaChartLine, 
  FaTable, 
  FaClock,
  FaServer,
  FaExchangeAlt,
  FaWallet,
  FaBullseye,
  FaChevronRight,
  FaDatabase,
  FaUsers,
  FaCoins,
  FaShieldAlt,
  FaStream
} from 'react-icons/fa';
import { FaNetworkWired } from 'react-icons/fa';


function Sidebar() {
  const { isOpen, setIsOpen } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const isActiveRoute = (path) => {
    return location.pathname === path;
  };

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const RUNTIME_LOGS_ENABLED = (() => {
    try {
      const raw = (process.env.REACT_APP_ENABLE_RUNTIME_LOGS || '').trim().toLowerCase();
      return raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on';
    } catch {
      return false;
    }
  })();

  return (
    <div className={`sidebar ${isOpen ? 'expanded' : 'collapsed'}`}>
      <div className="sidebar-header">
        <div className="avatar-container">
            <img 
              src="https://avatars.githubusercontent.com/u/6796665" 
              alt="User Avatar" 
              className="avatar-image"
            />
            {isOpen && <span className="avatar-name">{user?.email || 'Guest'}</span>}
            {isOpen && (
              <button onClick={handleSignOut} className="sign-out-button">
                <FaSignOutAlt className="sign-out-icon" />
                <span>Sign Out</span>
              </button>
            )}
        </div>
        <button 
          className={`toggle-arrow ${isOpen ? 'rotated' : ''}`} 
          onClick={toggleSidebar}
        >
          <FaChevronRight />
        </button>
      </div>
      
      <nav className="sidebar-nav">
        <ul>
          <li className={isActiveRoute('/ftso-submissions') ? 'active' : ''}>
              <Link to="/ftso-submissions" title="FTSO Submissions" onMouseEnter={() => cacheManager.prewarmForRoute('/ftso-submissions')} onFocus={() => cacheManager.prewarmForRoute('/ftso-submissions')} onTouchStart={() => cacheManager.prewarmForRoute('/ftso-submissions')}>
                <div className="nav-item">
                  <FaTable className="nav-icon" />
                  <span className="nav-text">FTSO Submissions</span>
                </div>
              </Link>
          </li>
          <li className={isActiveRoute('/fdc-submissions') ? 'active' : ''}>
              <Link to="/fdc-submissions" title="FDC Submissions" onMouseEnter={() => cacheManager.prewarmForRoute('/fdc-submissions')} onFocus={() => cacheManager.prewarmForRoute('/fdc-submissions')} onTouchStart={() => cacheManager.prewarmForRoute('/fdc-submissions')}>
                <div className="nav-item">
                  <FaShieldAlt className="nav-icon" />
                  <span className="nav-text">FDC Submissions</span>
                </div>
              </Link>
          </li>
          <li className={isActiveRoute('/ftso-performance') ? 'active' : ''}>
            <Link to="/ftso-performance" title="Performance" onMouseEnter={() => cacheManager.prewarmForRoute('/ftso-performance')} onFocus={() => cacheManager.prewarmForRoute('/ftso-performance')} onTouchStart={() => cacheManager.prewarmForRoute('/ftso-performance')}>
              <div className="nav-item">
                <FaChartLine className="nav-icon" />
                <span className="nav-text">Performance</span>
              </div>
            </Link>
          </li>
          <li className={isActiveRoute('/ftso-summary') ? 'active' : ''}>
            <Link to="/ftso-summary" title="Hit Rates" onMouseEnter={() => cacheManager.prewarmForRoute('/ftso-summary')} onFocus={() => cacheManager.prewarmForRoute('/ftso-summary')} onTouchStart={() => cacheManager.prewarmForRoute('/ftso-summary')}>
              <div className="nav-item">
                <FaBullseye className="nav-icon" />
                <span className="nav-text">Hit Rates</span>
              </div>
            </Link>
          </li>
          <li className={isActiveRoute('/fast-updates') ? 'active' : ''}>
            <Link to="/fast-updates" title="Fast Updates" onMouseEnter={() => cacheManager.prewarmForRoute('/fast-updates')} onFocus={() => cacheManager.prewarmForRoute('/fast-updates')} onTouchStart={() => cacheManager.prewarmForRoute('/fast-updates')}>
              <div className="nav-item">
                <FaClock className="nav-icon" />
                <span className="nav-text">Fast Updates</span>
              </div>
            </Link>
          </li>
          <li className={isActiveRoute('/nodes') ? 'active' : ''}>
            <Link to="/nodes" title="Nodes" onMouseEnter={() => cacheManager.prewarmForRoute('/nodes')} onFocus={() => cacheManager.prewarmForRoute('/nodes')} onTouchStart={() => cacheManager.prewarmForRoute('/nodes')}>
              <div className="nav-item">
                <FaNetworkWired className="nav-icon" />
                <span className="nav-text">Nodes</span>
              </div>
            </Link>
          </li>          
          <li className={isActiveRoute('/wallets') ? 'active' : ''}>
            <Link to="/wallets" title="Addresses" onMouseEnter={() => cacheManager.prewarmForRoute('/wallets')} onFocus={() => cacheManager.prewarmForRoute('/wallets')} onTouchStart={() => cacheManager.prewarmForRoute('/wallets')}>
              <div className="nav-item">
                <FaWallet className="nav-icon" />
                <span className="nav-text">Addresses</span>
              </div>
            </Link>
          </li>
          <li className={isActiveRoute('/indexers') ? 'active' : ''}>
            <Link to="/indexers" title="Indexers" onMouseEnter={() => cacheManager.prewarmForRoute('/indexers')} onFocus={() => cacheManager.prewarmForRoute('/indexers')} onTouchStart={() => cacheManager.prewarmForRoute('/indexers')}>
              <div className="nav-item">
                <FaDatabase className="nav-icon" />
                <span className="nav-text">Indexers</span>
              </div>
            </Link>
          </li>
          <li className={isActiveRoute('/disk-usage') ? 'active' : ''}>
            <Link to="/disk-usage" title="Disk Usage" onMouseEnter={() => cacheManager.prewarmForRoute('/disk-usage')} onFocus={() => cacheManager.prewarmForRoute('/disk-usage')} onTouchStart={() => cacheManager.prewarmForRoute('/disk-usage')}>
              <div className="nav-item">
                <FaServer className="nav-icon" />
                <span className="nav-text">Disk Usage</span>
              </div>
            </Link>
          </li>
          <li className={isActiveRoute('/validators') ? 'active' : ''}>
            <Link to="/validators" title="Validators" onMouseEnter={() => cacheManager.prewarmForRoute('/validators')} onFocus={() => cacheManager.prewarmForRoute('/validators')} onTouchStart={() => cacheManager.prewarmForRoute('/validators')}>
              <div className="nav-item">
                <FaUsers className="nav-icon" />
                <span className="nav-text">Validators</span>
              </div>
            </Link>
          </li>
          {RUNTIME_LOGS_ENABLED && (
            <li className={isActiveRoute('/runtime-logs') ? 'active' : ''}>
              <Link to="/runtime-logs" title="Runtime Logs" onMouseEnter={() => cacheManager.prewarmForRoute('/runtime-logs')} onFocus={() => cacheManager.prewarmForRoute('/runtime-logs')} onTouchStart={() => cacheManager.prewarmForRoute('/runtime-logs')}>
                <div className="nav-item">
                  <FaStream className="nav-icon" />
                  <span className="nav-text">Runtime Logs</span>
                </div>
              </Link>
            </li>
          )}
        </ul>
      </nav>
      <div className="sidebar-footer">
        <div className="version-info">
          <span className="version-number">v{VERSION.number}</span>
          <span className="version-date">{VERSION.date}</span>
        </div>
      </div>
    </div>
  );
}

export default React.memo(Sidebar);